import { SourcingEventStatus, type SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { autoKarensDays } from "../../../sourcing-events/buyer/public/utils/karens";
import type { MilestoneKey } from "../../../sourcing-events/buyer/public/utils/milestones";

export interface TimelinePhase {
  visible: boolean;
  idle: boolean;
  activeKey: MilestoneKey | null;
  completedKeys: MilestoneKey[];
}

// Mirrors MILESTONE_NODES order. The Karens (standstillExpiry) phase is dropped for procurements
// with no statutory standstill — Part II/I/outside-FOA, per Standstill.kt / FOA § 25-2.
const FULL_PHASE_ORDER: readonly MilestoneKey[] = [
  "announcement",
  "bidDeadline",
  "evaluationStart",
  "awardDecision",
  "standstillExpiry",
  "contractSigning",
];

export const phasesFor = (event: SourcingEventV2Dto): MilestoneKey[] => {
  const hasStandstill = autoKarensDays(event.publicContext.foaPart ?? null) != null;
  return FULL_PHASE_ORDER.filter((key) => key !== "standstillExpiry" || hasStandstill);
};

// Drafts have no read view — the buyer is redirected to the wizard — so the timeline never renders
// for them in practice; hidden here defensively.
const HIDDEN_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([SourcingEventStatus.Draft]);

// "Idle" = visible but greyed: the procurement isn't progressing through phases right now. Covers
// cancellation and the pre-announcement states (awaiting publication / publication failed), where no
// phase is active yet. The status tag above the timeline names which reason applies.
const IDLE_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.PublicationPending,
  SourcingEventStatus.PublicationFailed,
]);

const HIDDEN: TimelinePhase = { visible: false, idle: false, activeKey: null, completedKeys: [] };

const hasPassed = (iso: string | null | undefined, now: number): boolean =>
  iso != null && now >= Date.parse(iso);

// `now` is injected (epoch ms) rather than read from a clock, so the date-driven transitions
// (① → ② at bid deadline, ⑤ → ⑥ at standstill expiry) are deterministic in tests.
export const deriveTimelinePhase = (event: SourcingEventV2Dto, now: number): TimelinePhase => {
  const { status } = event;
  if (HIDDEN_STATUSES.has(status)) return HIDDEN;

  const phases = phasesFor(event);

  if (IDLE_STATUSES.has(status)) {
    return { visible: true, idle: true, activeKey: null, completedKeys: [] };
  }
  if (status === SourcingEventStatus.Closed) {
    return { visible: true, idle: false, activeKey: null, completedKeys: [...phases] };
  }

  const activeKey = activeKeyFor(event, now, phases);
  if (activeKey == null) return HIDDEN;

  const activeIndex = phases.indexOf(activeKey);
  return { visible: true, idle: false, activeKey, completedKeys: phases.slice(0, activeIndex) };
};

const activeKeyFor = (
  event: SourcingEventV2Dto,
  now: number,
  phases: readonly MilestoneKey[],
): MilestoneKey | null => {
  switch (event.status) {
    case SourcingEventStatus.Published:
      return hasPassed(event.eventData.bidSubmissionDeadline, now) ? "bidDeadline" : "announcement";
    case SourcingEventStatus.Evaluation:
      return "evaluationStart";
    case SourcingEventStatus.AwardDecision:
      return "awardDecision";
    case SourcingEventStatus.Awarded:
      if (!phases.includes("standstillExpiry")) return "contractSigning";
      return hasPassed(event.award?.standstillExpiresAt, now) ? "contractSigning" : "standstillExpiry";
    default:
      return null;
  }
};
